import React, { useState, useEffect } from 'react';
import { api } from './App';

const TYPE_OPTIONS = [
  { value: 'bug',     label: "Something's broken" },
  { value: 'feature', label: 'Feature request' },
  { value: 'general', label: 'General feedback' },
];

export default function Feedback() {
  const [step, setStep] = useState('form'); // 'form' | 'followup' | 'thanks'
  const [fbType, setFbType] = useState('general');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [followupId, setFollowupId] = useState(null);
  const [followupQuestion, setFollowupQuestion] = useState('');
  const [followupAnswer, setFollowupAnswer] = useState('');
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api('/api/feedback').then(data => {
      if (Array.isArray(data)) setHistory(data);
    });
  }, [step]);

  const submit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type: fbType,
          description: description.trim(),
          context_route: window.location.pathname,
          context_browser: navigator.userAgent,
        }),
      });
      if (res.error) { setError(res.error); return; }
      if (res.followup_question) {
        setFollowupId(res.id);
        setFollowupQuestion(res.followup_question);
        setStep('followup');
      } else {
        setStep('thanks');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitFollowup = async () => {
    if (!followupAnswer.trim()) { setStep('thanks'); return; }
    setSavingFollowup(true);
    try {
      await api(`/api/feedback/${followupId}/followup`, {
        method: 'POST',
        body: JSON.stringify({ answer: followupAnswer.trim() }),
      });
    } finally {
      setSavingFollowup(false);
      setStep('thanks');
    }
  };

  const reset = () => {
    setStep('form');
    setFbType('general');
    setDescription('');
    setFollowupId(null);
    setFollowupQuestion('');
    setFollowupAnswer('');
    setError(null);
  };

  return (
    <div className="feedback-page">
      <div className="feedback-intro">
        <div className="feedback-title">Help us improve SQETrack</div>
        <p className="feedback-desc">Tell us what you were doing, what happened, and what you expected. Specific details help us fix things faster.</p>
      </div>

      {step === 'form' && (
        <div className="feedback-form-card">
          <div className="feedback-type-row">
            {TYPE_OPTIONS.map(opt => (
              <label key={opt.value} className={`feedback-type-btn ${fbType === opt.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="fb_type"
                  value={opt.value}
                  checked={fbType === opt.value}
                  onChange={() => setFbType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <textarea
            className="feedback-textarea"
            placeholder="What happened or what would you like to see?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) submit(); }}
            rows={5}
          />
          {error && <div className="feedback-error">{error}</div>}
          <div className="feedback-input-row">
            <span style={{ fontSize: 11, color: 'var(--hint)' }}>Cmd+Enter to submit</span>
            <button
              className="feedback-submit-btn"
              onClick={submit}
              disabled={submitting || !description.trim()}
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        </div>
      )}

      {step === 'followup' && (
        <div className="feedback-form-card">
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: '.5rem', color: 'var(--text)' }}>
              One quick question:
            </div>
            <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.5 }}>{followupQuestion}</div>
          </div>
          <textarea
            className="feedback-textarea"
            placeholder="Your answer (optional)…"
            value={followupAnswer}
            onChange={e => setFollowupAnswer(e.target.value)}
            rows={4}
          />
          <div className="feedback-input-row">
            <button className="feedback-submit-btn btn-ghost" onClick={() => setStep('thanks')} style={{ marginRight: '.5rem' }}>
              Skip
            </button>
            <button className="feedback-submit-btn" onClick={submitFollowup} disabled={savingFollowup}>
              {savingFollowup ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {step === 'thanks' && (
        <div className="feedback-form-card feedback-thanks">
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: '.5rem' }}>Thanks — we read everything.</div>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
            Your feedback has been sent. If you reported a bug, we'll look into it.
          </p>
          <button className="feedback-submit-btn" onClick={reset}>Send more feedback</button>
        </div>
      )}

      {history.length > 0 && (
        <div className="feedback-history">
          <div className="feedback-section-title" style={{ marginBottom: '0.75rem' }}>
            Your previous feedback
          </div>
          {history.map(f => (
            <div key={f.id} className={`feedback-item ${f.resolved ? 'feedback-item-resolved' : ''}`}>
              <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.25rem' }}>
                <span style={{ fontSize: 11, background: 'var(--bg-alt)', padding: '2px 6px', borderRadius: 4, color: 'var(--muted)' }}>
                  {f.type || 'general'}
                </span>
                {f.resolved && (
                  <span style={{ fontSize: 11, color: '#22c55e' }}>✓ Resolved</span>
                )}
              </div>
              <div className="feedback-item-text">{f.description || f.text}</div>
              <div className="feedback-item-date">{f.created_at?.slice(0, 10)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
